import { useMemo } from 'react'
import type { AsyncState } from '../../platform/async/Async'
import { totalsOf, type Totals } from './accountMoney'
import { currenciesOf } from './AccountsSummary'
import { useAccounts, type Account } from './useAccounts'

export type NetWorth = {
  /** `totalsOf` on every account, `null` when currencies mix (D21). */
  totals: Totals | null
  /** One subtotal per currency, by the rule the Accounts page's `PerCurrencySubtotals` uses. */
  byCurrency: { currency: string; totals: Totals | null }[]
}

function netWorthOf(accounts: Account[]): NetWorth {
  return {
    totals: totalsOf(accounts),
    byCurrency: currenciesOf(accounts).map((currency) => ({
      currency,
      totals: totalsOf(accounts.filter((a) => a.currency === currency)),
    })),
  }
}

/**
 * The net-worth figure M2 Overview shows — the very number `/accounts` shows.
 *
 * It adds no arithmetic of its own: `totalsOf` is M5's pre-freeze computation,
 * and this hook only maps `useAccounts()`'s state around it. Every state
 * (loading, empty, error, stale with its `asOf`) passes through unchanged, so
 * a surface registered `synced` renders it exactly as the Accounts page does.
 */
export function useNetWorth(): AsyncState<NetWorth> {
  const state = useAccounts()
  return useMemo<AsyncState<NetWorth>>(() => {
    switch (state.status) {
      case 'success':
        return { status: 'success', data: netWorthOf(state.data) }
      case 'stale':
        return { status: 'stale', data: netWorthOf(state.data), asOf: state.asOf }
      default:
        return state
    }
  }, [state])
}
